import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useFloating, autoUpdate, offset, flip, shift, arrow } from "@floating-ui/react"

export function Tooltip({ target, placement = "top", delay = { show: 0, hide: 100 }, children }) {
  const [isOpen, setIsOpen] = useState(false)
  const arrowElement = useRef(null)
  const showTimeout = useRef(null)
  const hideTimeout = useRef(null)

  const { refs, floatingStyles, placement: computedPlacement, middlewareData } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowElement })]
  })

  useEffect(() => {
    const referenceElement = document.getElementById(target)
    if (!referenceElement) { return }

    refs.setReference(referenceElement)

    const show = () => {
      clearTimeout(hideTimeout.current)
      showTimeout.current = setTimeout(() => setIsOpen(true), delay.show)
    }
    const hide = () => {
      clearTimeout(showTimeout.current)
      hideTimeout.current = setTimeout(() => setIsOpen(false), delay.hide)
    }

    referenceElement.addEventListener("mouseenter", show)
    referenceElement.addEventListener("mouseleave", hide)
    referenceElement.addEventListener("focus", show)
    referenceElement.addEventListener("blur", hide)

    return () => {
      referenceElement.removeEventListener("mouseenter", show)
      referenceElement.removeEventListener("mouseleave", hide)
      referenceElement.removeEventListener("focus", show)
      referenceElement.removeEventListener("blur", hide)
      clearTimeout(showTimeout.current)
      clearTimeout(hideTimeout.current)
    }
  }, [target])

  if (!isOpen) { return null }

  const side = computedPlacement.split("-")[0]
  const arrowData = middlewareData.arrow

  return createPortal(
    <div
      ref={refs.setFloating}
      role="tooltip"
      className={`tooltip bs-tooltip-${side} show`}
      style={floatingStyles}
      onMouseEnter={() => clearTimeout(hideTimeout.current)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div
        ref={arrowElement}
        className="arrow"
        style={{
          left: arrowData?.x != null ? `${arrowData.x}px` : "",
          top: arrowData?.y != null ? `${arrowData.y}px` : ""
        }}
      />
      <div className="tooltip-inner">{children}</div>
    </div>,
    document.body
  )
}
